import { useQueryClient } from '@tanstack/react-query';
import { ContactRound, Mail, MessageSquare, Pencil, Phone, Shield, UserX, type LucideIcon } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import { canManageAvatarOf, type MemberDTO } from '../../shared/dto/members';
import { ApiFailure, isNetworkFailure } from '../api/client';
import { MEMBERS_KEY, memberKey, useMember } from '../api/members';
import { isQueryGone, isQueryKeyGone } from '../api/queryClient';
import { useMe } from '../auth/AuthProvider';
import { MemberAvatarEditor } from '../components/AvatarEditor';
import { Page } from '../components/layout';
import { Avatar, SectionBadge } from '../components/people';
import { useToast } from '../components/toast';
import { Card, CenteredSpinner, EmptyState, ErrorBox, cx } from '../components/ui';
import { useT } from '../i18n';
import { LeaveBadge, TitleBadge } from './choir/MemberRow';
import { shareOrDownloadVCard } from './choir/shareContact';
import { normalizePhone } from './choir/vcard';

function ActionLink({ href, icon: Icon, label, onClick }: { href?: string; icon: LucideIcon; label: string; onClick?: () => void }) {
  const cls =
    'flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-3 py-3 text-[16px] font-semibold text-[var(--accent)] transition hover:bg-[var(--accent-soft)] active:bg-[var(--accent-soft)]';
  const inner = (
    <>
      <Icon className="size-7" aria-hidden />
      <span className="text-center leading-tight">{label}</span>
    </>
  );
  return href ? (
    <a href={href} className={cls}>
      {inner}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

function InfoRow({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-4 px-5 py-3.5">
      <Icon className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] text-[var(--muted)]">{label}</div>
        <div className="text-[17px] break-words select-text">{children}</div>
      </div>
    </div>
  );
}

function Profile({ m }: { m: MemberDTO }) {
  const t = useT();
  const toast = useToast();
  const me = useMe();
  const isMe = me?.id === m.id;
  const canAdmin = !!me && roleAtLeast(me.role, 'editor');
  // Editors (the conductor) and admins may add, change or remove another member's photo; your own is in Settings.
  const canPhoto = !!me && !isMe && canManageAvatarOf(me, m);
  const phone = m.phone?.trim() || null;
  const email = m.email?.trim() || null;
  const hasContact = !!(phone || email);

  const addContact = async () => {
    try {
      const sectionName = m.section ? t(`section.${m.section}`) : null;
      await shareOrDownloadVCard({
        fullName: m.fullName,
        phone,
        email,
        title: m.title,
        org: t('app.name'),
        note: sectionName ? t('choir.vcard_note', { section: sectionName }) : t('app.name'),
      });
    } catch {
      toast.show(t('choir.vcard_failed'), { tone: 'error' });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-3 pt-2 text-center">
        <Avatar userId={m.id} name={m.fullName} rev={m.avatarRev} section={m.section} size={176} className="shadow-[var(--shadow-card)]" />
        <h2 className="text-[28px] leading-tight font-bold tracking-tight">{m.fullName}</h2>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <SectionBadge section={m.section} />
          {m.title ? <TitleBadge>{m.title}</TitleBadge> : null}
          {m.status === 'leave' ? <LeaveBadge /> : null}
        </div>
        {canPhoto ? <MemberAvatarEditor key={m.id} member={m} className="mt-1 justify-center" /> : null}
      </div>

      {hasContact ? (
        <>
          <div className={cx('grid gap-3', phone && email ? 'grid-cols-2 sm:grid-cols-4' : phone ? 'grid-cols-3' : 'grid-cols-2')}>
            {phone ? <ActionLink href={`tel:${normalizePhone(phone)}`} icon={Phone} label={t('choir.call')} /> : null}
            {phone ? <ActionLink href={`sms:${normalizePhone(phone)}`} icon={MessageSquare} label={t('choir.sms')} /> : null}
            {email ? <ActionLink href={`mailto:${encodeURIComponent(email).replace(/%40/g, '@')}`} icon={Mail} label={t('choir.email')} /> : null}
            <ActionLink icon={ContactRound} label={t('choir.add_contact')} onClick={() => void addContact()} />
          </div>
          <Card className="divide-y divide-[var(--line)]">
            {phone ? (
              <InfoRow icon={Phone} label={t('choir.phone')}>
                {phone}
              </InfoRow>
            ) : null}
            {email ? (
              <InfoRow icon={Mail} label={t('choir.email_label')}>
                {email}
              </InfoRow>
            ) : null}
          </Card>
        </>
      ) : !isMe ? (
        <p className="text-center text-[var(--muted)]">{t('choir.no_contact')}</p>
      ) : null}

      {isMe || canAdmin ? (
        <div className="flex flex-wrap justify-center gap-3">
          {isMe ? (
            <Link
              to="/settings"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 font-semibold text-[var(--ink)] hover:bg-[var(--surface-2)]"
            >
              <Pencil className="size-5" aria-hidden />
              {t('choir.edit_profile')}
            </Link>
          ) : null}
          {canAdmin ? (
            <Link
              to={`/admin/members/${m.id}`}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl px-4 font-semibold text-[var(--accent)] hover:bg-[var(--accent-soft)]"
            >
              <Shield className="size-5" aria-hidden />
              {t('choir.edit_admin')}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 404: the member was deactivated or erased (or never existed). TanStack keeps the old (persisted)
 * profile next to that error — name, phone and e-mail must not be shown or kept: it is not persisted
 * again (shouldPersistQuery) and leaves the cached directory too. Sticky (isQueryGone): a later
 * refetch that fails offline or with a 5xx replaces the 404 as the error but keeps the data.
 */
function isMemberGone(e: unknown): boolean {
  return e instanceof ApiFailure && (e.status === 404 || e.status === 403);
}

export default function MemberScreen() {
  const { userId } = useParams();
  const location = useLocation();
  const t = useT();
  const qc = useQueryClient();
  const q = useMember(userId);
  const gone = isMemberGone(q.error) || (!!userId && isQueryKeyGone(qc, memberKey(userId)));

  useEffect(() => {
    if (!gone || !userId) return;
    qc.setQueryData<MemberDTO[]>(MEMBERS_KEY, (list) => (list?.some((m) => m.id === userId) ? list.filter((m) => m.id !== userId) : list));
  }, [gone, userId, qc]);
  // When the page closes the stale profile leaves memory too (not while observed: a removed query is
  // refetched by the next render) — unless fresh data arrived meanwhile.
  useEffect(() => {
    if (!gone || !userId) return;
    return () => {
      const query = qc.getQueryCache().find({ queryKey: memberKey(userId), exact: true });
      if (query && isQueryGone(query)) qc.removeQueries({ queryKey: memberKey(userId), exact: true });
    };
  }, [gone, userId, qc]);

  let body;
  if (q.data && !gone) body = <Profile m={q.data} />;
  // offlineFirst: with no connection and nothing cached the query pauses instead of failing.
  else if (q.isPending && q.fetchStatus === 'paused')
    body = (
      <EmptyState icon={UserX} title={t('common.offline')}>
        {t('common.no_data_offline')}
      </EmptyState>
    );
  else if (q.isPending) body = <CenteredSpinner />;
  else if (gone)
    body = (
      <EmptyState icon={UserX} title={t('choir.not_found')}>
        {t('choir.not_found_hint')}
      </EmptyState>
    );
  else if (isNetworkFailure(q.error))
    body = (
      <EmptyState icon={UserX} title={t('common.offline')}>
        {t('common.no_data_offline')}
      </EmptyState>
    );
  else
    body = (
      <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
        {t('common.error')}
      </ErrorBox>
    );

  return (
    <Page title={t('choir.member')} back={location.key === 'default' ? '/choir' : true}>
      <div className="mx-auto max-w-xl">{body}</div>
    </Page>
  );
}
